import type { MonsterSpecies } from '@mn/shared';

// ── Beast roster: ~24 original Monster-Hunter-style beasts (NO Pokémon IP). ──
// Covers all 8 elements with early/mid/late tiers + a few rare "apex" beasts.
// `lore` is a content-only flavor field layered on top of the shared MonsterSpecies.

export type BeastSpecies = MonsterSpecies & { lore: string };

export const BEASTS: BeastSpecies[] = [
  // ───────────────────────── FLAME line (starter A) ─────────────────────────
  {
    id: 'sp_emberling', name: 'Emberling', element: 'flame',
    baseStats: { hp: 42, atk: 52, def: 40, spd: 58 },
    evolvesToId: 'sp_cindermaw', evolveLevel: 16,
    spriteKey: 'beast/emberling', catchRate: 0.45,
    lore: 'A cub whose mane sparks when it is startled; trackers follow the scorch trail it leaves.',
  },
  {
    id: 'sp_cindermaw', name: 'Cindermaw', element: 'flame',
    baseStats: { hp: 64, atk: 78, def: 58, spd: 72 },
    evolvesToId: 'sp_pyrothar', evolveLevel: 34,
    spriteKey: 'beast/cindermaw', catchRate: 0.28,
    lore: 'Its jaws smelt iron. Wilds smiths once paid bounties to harvest its breath-forged fangs.',
  },
  {
    id: 'sp_pyrothar', name: 'Pyrothar', element: 'flame',
    baseStats: { hp: 92, atk: 116, def: 84, spd: 96 },
    spriteKey: 'beast/pyrothar', catchRate: 0.12,
    lore: 'An apex predator of the ash basins; its roar reignites dead volcanoes.',
  },

  // ───────────────────────── TIDE line (starter B) ─────────────────────────
  {
    id: 'sp_dripfin', name: 'Dripfin', element: 'tide',
    baseStats: { hp: 46, atk: 44, def: 48, spd: 54 },
    evolvesToId: 'sp_surgeon', evolveLevel: 16,
    spriteKey: 'beast/dripfin', catchRate: 0.45,
    lore: 'A tidepool hatchling that breathes through gill-frills; it hums to call the current.',
  },
  {
    id: 'sp_surgeon', name: 'Surgeon', element: 'tide',
    baseStats: { hp: 70, atk: 66, def: 72, spd: 70 },
    evolvesToId: 'sp_leviadon', evolveLevel: 36,
    spriteKey: 'beast/surgeon', catchRate: 0.26,
    lore: 'Rides riptides like a blade. Coastal hunters say it sharpens its crest on shipwrecks.',
  },
  {
    id: 'sp_leviadon', name: 'Leviadon', element: 'tide',
    baseStats: { hp: 104, atk: 100, def: 102, spd: 78 },
    spriteKey: 'beast/leviadon', catchRate: 0.1,
    lore: 'A drowned-trench colossus; whole reefs grow on its back between centuries of slumber.',
  },

  // ───────────────────────── VERDANT line (starter C) ─────────────────────────
  {
    id: 'sp_sproutling', name: 'Sproutling', element: 'verdant',
    baseStats: { hp: 50, atk: 46, def: 50, spd: 46 },
    evolvesToId: 'sp_bramblok', evolveLevel: 16,
    spriteKey: 'beast/sproutling', catchRate: 0.45,
    lore: 'Photosynthesizes through leaf-ears; sleeps curled like a fern waiting for dawn.',
  },
  {
    id: 'sp_bramblok', name: 'Bramblok', element: 'verdant',
    baseStats: { hp: 78, atk: 70, def: 80, spd: 56 },
    evolvesToId: 'sp_grovewarden', evolveLevel: 36,
    spriteKey: 'beast/bramblok', catchRate: 0.26,
    lore: 'A thorn-plated grazer; its hide hardens into living bark when threatened.',
  },
  {
    id: 'sp_grovewarden', name: 'Grovewarden', element: 'verdant',
    baseStats: { hp: 112, atk: 92, def: 110, spd: 64 },
    spriteKey: 'beast/grovewarden', catchRate: 0.1,
    lore: 'Ancient root-beast that walks the deep canopy; forests regrow in its footprints.',
  },

  // ───────────────────────── STONE ─────────────────────────
  {
    id: 'sp_pebbit', name: 'Pebbit', element: 'stone',
    baseStats: { hp: 54, atk: 50, def: 64, spd: 36 },
    evolvesToId: 'sp_boulderhide', evolveLevel: 20,
    spriteKey: 'beast/pebbit', catchRate: 0.42,
    lore: 'A scree-dweller that disguises itself as a loose rock until prey wanders too close.',
  },
  {
    id: 'sp_boulderhide', name: 'Boulderhide', element: 'stone',
    baseStats: { hp: 96, atk: 84, def: 116, spd: 40 },
    spriteKey: 'beast/boulderhide', catchRate: 0.2,
    lore: 'A cliffside bruiser; landslides are often just Boulderhide rolling downhill in a temper.',
  },

  // ───────────────────────── GALE ─────────────────────────
  {
    id: 'sp_zephyx', name: 'Zephyx', element: 'gale',
    baseStats: { hp: 44, atk: 54, def: 38, spd: 76 },
    evolvesToId: 'sp_skytalon', evolveLevel: 22,
    spriteKey: 'beast/zephyx', catchRate: 0.4,
    lore: 'A glider-kit that sleeps clinging to updrafts; it never truly touches the ground.',
  },
  {
    id: 'sp_skytalon', name: 'Skytalon', element: 'gale',
    baseStats: { hp: 76, atk: 98, def: 64, spd: 118 },
    spriteKey: 'beast/skytalon', catchRate: 0.16,
    lore: 'A peak-circling raptor; its dive shatters the sound of the wind around it.',
  },

  // ───────────────────────── VENOM ─────────────────────────
  {
    id: 'sp_grublin', name: 'Grublin', element: 'venom',
    baseStats: { hp: 48, atk: 52, def: 46, spd: 50 },
    evolvesToId: 'sp_neurolisk', evolveLevel: 24,
    spriteKey: 'beast/grublin', catchRate: 0.42,
    lore: 'A bog larva that weeps a numbing sap; predators that bite it rarely bite again.',
  },
  {
    id: 'sp_neurolisk', name: 'Neurolisk', element: 'venom',
    baseStats: { hp: 84, atk: 104, def: 70, spd: 92 },
    spriteKey: 'beast/neurolisk', catchRate: 0.16,
    lore: 'A swamp serpent whose stare slows the heart; its venom is brewed into rare toxins.',
  },

  // ───────────────────────── FROST ─────────────────────────
  {
    id: 'sp_frostkit', name: 'Frostkit', element: 'frost',
    baseStats: { hp: 50, atk: 48, def: 52, spd: 54 },
    evolvesToId: 'sp_glacior', evolveLevel: 24,
    spriteKey: 'beast/frostkit', catchRate: 0.42,
    lore: 'A snowline pup that breathes frost-fog to hide its tracks from hunters.',
  },
  {
    id: 'sp_glacior', name: 'Glacior', element: 'frost',
    baseStats: { hp: 100, atk: 96, def: 100, spd: 70 },
    spriteKey: 'beast/glacior', catchRate: 0.16,
    lore: 'A glacier-born brute; the cold rolling off its hide can freeze a river mid-flow.',
  },

  // ───────────────────────── BEAST (neutral common line) ─────────────────────────
  {
    id: 'sp_fennec', name: 'Fennec', element: 'beast',
    baseStats: { hp: 46, atk: 48, def: 44, spd: 60 },
    evolvesToId: 'sp_dunestalker', evolveLevel: 18,
    spriteKey: 'beast/fennec', catchRate: 0.5,
    lore: 'A scavenger with enormous ears; the first beast most hunters ever track.',
  },
  {
    id: 'sp_dunestalker', name: 'Dunestalker', element: 'beast',
    baseStats: { hp: 80, atk: 86, def: 72, spd: 90 },
    spriteKey: 'beast/dunestalker', catchRate: 0.22,
    lore: 'A pack-hunting prowler; it runs prey to exhaustion across open ground.',
  },
  {
    id: 'sp_grazeling', name: 'Grazeling', element: 'beast',
    baseStats: { hp: 60, atk: 40, def: 56, spd: 48 },
    spriteKey: 'beast/grazeling', catchRate: 0.55,
    lore: 'A docile herd-beast; common, calm, and surprisingly stubborn when cornered.',
  },

  // ───────────────────────── RARE / APEX (Badlands-tier) ─────────────────────────
  {
    id: 'sp_obsidrake', name: 'Obsidrake', element: 'flame',
    baseStats: { hp: 118, atk: 132, def: 96, spd: 104 },
    spriteKey: 'beast/obsidrake', catchRate: 0.05,
    lore: 'A volcanic-glass wyrm of the Badlands; its scales cut like knapped obsidian.',
  },
  {
    id: 'sp_voidmaw', name: 'Voidmaw', element: 'venom',
    baseStats: { hp: 110, atk: 138, def: 90, spd: 108 },
    spriteKey: 'beast/voidmaw', catchRate: 0.05,
    lore: 'A blight-touched apex; the ground it walks withers for a season after.',
  },
  {
    id: 'sp_aurorath', name: 'Aurorath', element: 'frost',
    baseStats: { hp: 124, atk: 120, def: 116, spd: 96 },
    spriteKey: 'beast/aurorath', catchRate: 0.04,
    lore: 'A legendary frost titan; the polar lights are said to be its breath.',
  },
  {
    id: 'sp_titanok', name: 'Titanok', element: 'stone',
    baseStats: { hp: 150, atk: 128, def: 150, spd: 52 },
    spriteKey: 'beast/titanok', catchRate: 0.03,
    lore: 'A mountain that learned to walk; the rarest quarry in all the Badlands.',
  },

  // ───────────────────── ULTRA-RARE (deep Cave) — declaration order fixes monsterId 25-29 ─────────────────────
  {
    id: 'sp_voltbloom', name: 'Voltbloom', element: 'verdant',
    baseStats: { hp: 116, atk: 130, def: 100, spd: 110 },
    spriteKey: 'beast/voltbloom', catchRate: 0.04,
    lore: 'A leaf-crowned drake that stores storm-charge in its sap; rare to the deep Cave.',
  },
  {
    id: 'sp_nyxwing', name: 'Nyxwing', element: 'beast',
    baseStats: { hp: 112, atk: 140, def: 92, spd: 116 },
    spriteKey: 'beast/nyxwing', catchRate: 0.04,
    lore: 'A shadow-winged wyrmling that hunts the lightless reaches of the Cave.',
  },
  {
    id: 'sp_voltfox', name: 'Voltfox', element: 'gale',
    baseStats: { hp: 104, atk: 134, def: 88, spd: 130 },
    spriteKey: 'beast/voltfox', catchRate: 0.04,
    lore: 'A static-furred prowler; the air crackles before you ever see it.',
  },
  {
    id: 'sp_cryolith', name: 'Cryolith', element: 'frost',
    baseStats: { hp: 130, atk: 118, def: 128, spd: 80 },
    spriteKey: 'beast/cryolith', catchRate: 0.03,
    lore: 'A crystal-spined golem grown from the Cave’s coldest seams.',
  },
  {
    id: 'sp_ferrok', name: 'Ferrok', element: 'stone',
    baseStats: { hp: 152, atk: 126, def: 152, spd: 50 },
    spriteKey: 'beast/ferrok', catchRate: 0.03,
    lore: 'A horned iron sentinel; its hide turns blades and claws alike.',
  },
  // ── Mythical venom line (community art): Solani → Solanoid. monsterId 30 / 31 (= BEASTS index +1),
  //    kept in lockstep with assets/data/monsters.json so the server resolves the same beasts. ──
  {
    id: 'sp_solani', name: 'Solani', element: 'venom',
    baseStats: { hp: 50, atk: 56, def: 44, spd: 60 },
    evolvesToId: 'sp_solanoid', evolveLevel: 30,
    spriteKey: 'beast/solani', catchRate: 0.05,
    lore: 'A spiny venom hatchling crowned in toxic plumage — mythical, rarely seen.',
  },
  {
    id: 'sp_solanoid', name: 'Solanoid', element: 'venom',
    baseStats: { hp: 120, atk: 134, def: 100, spd: 96 },
    spriteKey: 'beast/solanoid', catchRate: 0.02,
    lore: 'The winged mythical apex of the Solani line; its wingbeats spread a paralytic haze.',
  },
];

export const BEASTS_BY_ID: Record<string, BeastSpecies> = Object.fromEntries(
  BEASTS.map((b) => [b.id, b]),
);
